import {
  ApolloClient as ApolloClientV3,
  defaultDataIdFromObject as defaultDataIdFromObjectV3,
  InMemoryCache as InMemoryCacheV3,
  type NormalizedCacheObject,
  type StoreValue,
  type TypePolicies,
} from '@apollo/client';
import {
  defaultDataIdFromObject as defaultDataIdFromObjectV2,
  InMemoryCache as InMemoryCacheV2,
  IntrospectionFragmentMatcher,
} from 'apollo-cache-inmemory';
import ApolloClientV2 from 'apollo-client';
import {get} from 'lodash';

import {isApolloClientV3Enabled} from './apolloRuntime';
import {envIsDev, envIsIntegration} from './config';
import introspectionQueryResultData from './generated/fragmentTypes.json';
import objectTypeNames from './generated/objectTypeNames.json';
import {apolloLink} from './util/apollo';

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData,
});

const possibleTypes = Object.fromEntries(
  introspectionQueryResultData.__schema.types.map(type => [
    type.name,
    type.possibleTypes.map(possibleType => possibleType.name),
  ])
);

// Objects without a cache ID are embedded within their parent cache entry.
// Apollo Client v3 replaces those objects on write, whereas v2 merged their
// fields. Preserve the v2 behavior for the generated set of embedded types.
const typePolicies: TypePolicies = Object.fromEntries(
  objectTypeNames.map(name => [name, {merge: true}])
);

const appendCustomId = (
  defaultId: string | null | undefined,
  customIdType: string,
  customId: StoreValue | undefined
) => {
  if (customId == null || defaultId == null) {
    return defaultId;
  }
  return `${defaultId},${customIdType}:${customId}`;
};

// Default on; disable with `GQL_NO_QUERY_DEDUP` query param
// e.g., https://wandb.ai/?GQL_NO_QUERY_DEDUP
const shouldEnableQueryDeduplication = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return !urlParams.has('GQL_NO_QUERY_DEDUP');
};

export const parseRunId = (run: any) => atob(run.id).split(':')[2];

function cacheIdFromObject(
  object: Readonly<Record<string, any>>,
  defaultId: string | null | undefined
): string | null | undefined {
  if (object == null || defaultId == null) {
    return defaultId;
  }
  switch (object.__typename) {
    case 'LogLine':
      // LogLine IDs are scoped to a run rather than globally unique.
      return isApolloClientV3Enabled ? undefined : null;
    case 'Member':
      return appendCustomId(defaultId, 'role', get(object, 'role'));
    case 'OrganizationMember': {
      const adminId = appendCustomId(defaultId, 'admin', get(object, 'admin'));
      return appendCustomId(adminId, 'orgID', get(object, 'orgID'));
    }
    default:
      return defaultId;
  }
}

function createApolloV2Clients() {
  // Preserve the existing isolated, non-normalizing client behavior. Default
  // to no-cache so callers cannot accidentally write large query results.
  const noCacheClient = new ApolloClientV2({
    link: apolloLink as any,
    connectToDevTools: envIsDev || envIsIntegration,
    assumeImmutableResults: true,
    defaultOptions: {
      query: {fetchPolicy: 'no-cache'},
      watchQuery: {fetchPolicy: 'no-cache'},
    },
    cache: new InMemoryCacheV2({dataIdFromObject: () => null}),
    queryDeduplication: shouldEnableQueryDeduplication(),
  });

  const client = new ApolloClientV2({
    typeDefs: [],
    resolvers: {},
    link: apolloLink as any,
    connectToDevTools: envIsDev || envIsIntegration,
    cache: new InMemoryCacheV2({
      fragmentMatcher,
      dataIdFromObject: object =>
        cacheIdFromObject(object, defaultDataIdFromObjectV2(object)) as
          | string
          | null,
    }),
    assumeImmutableResults: true,
    queryDeduplication: shouldEnableQueryDeduplication(),
  });

  // Apollo v2 retains every mutation for devtools, causing a large leak for
  // auto-saved view specs. This internal store no longer exists in v3.
  const mutationStore = client.queryManager.mutationStore;
  const mutationStoreInitMutation: typeof mutationStore.initMutation =
    function (this: any, mutationId) {
      this.store[mutationId] = {
        mutation: 'removed, see WB-3579',
        variables: {},
        loading: true,
        error: null,
      };
    };
  mutationStore.initMutation = mutationStoreInitMutation;

  return {client, noCacheClient};
}

function createApolloV3Clients() {
  const client = new ApolloClientV3({
    typeDefs: [],
    resolvers: {},
    link: apolloLink,
    connectToDevTools: envIsDev || envIsIntegration,
    cache: new InMemoryCacheV3({
      possibleTypes,
      typePolicies,
      dataIdFromObject: object =>
        cacheIdFromObject(object, defaultDataIdFromObjectV3(object)) as
          | string
          | undefined,
    }),
    assumeImmutableResults: true,
    queryDeduplication: shouldEnableQueryDeduplication(),
  });

  // Apollo v3's no-cache fetch policy does not read from or write to the
  // InMemoryCache. Every transitional no-cache client call site supplies that
  // policy explicitly, so v3 does not need a second client or cache.
  // https://www.apollographql.com/docs/react/v3/caching/advanced-topics/#bypassing-the-cache
  return {client, noCacheClient: client};
}

const selectedClients = isApolloClientV3Enabled
  ? createApolloV3Clients()
  : createApolloV2Clients();

export const apolloClient =
  selectedClients.client as unknown as ApolloClientV3<NormalizedCacheObject>;
export const apolloClientNoCache =
  selectedClients.noCacheClient as unknown as ApolloClientV3<NormalizedCacheObject>;
